import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { guestAPI } from '../lib/api';
import { formatDuration, getStatusColor } from '../lib/utils';
import {
  Play, RotateCcw, Clock, Share2, CheckCircle, ArrowLeftRight,
  User, MapPin, Users as UsersIcon, ArrowRight,
} from 'lucide-react';

function GuestMatchCreate({ onCreated }) {
  const [guestName, setGuestName] = useState('');
  const [guestAgeGroup, setGuestAgeGroup] = useState('');
  const [guestLocation, setGuestLocation] = useState('');
  const [matchType, setMatchType] = useState('singles');
  const [p1Name, setP1Name] = useState('');
  const [p2Name, setP2Name] = useState('');
  const [p1Partner, setP1Partner] = useState('');
  const [p2Partner, setP2Partner] = useState('');
  const [bestOf, setBestOf] = useState('1');
  const [pointsPerGame, setPointsPerGame] = useState('21');
  const [loading, setLoading] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!guestName.trim()) { toast.error('Enter your name'); return; }
    if (!p1Name.trim() || !p2Name.trim()) { toast.error('Enter both player names'); return; }
    if (matchType === 'doubles' && (!p1Partner.trim() || !p2Partner.trim())) { toast.error('Enter all partner names for doubles'); return; }
    setLoading(true);
    try {
      const res = await guestAPI.createMatch({
        guest_name: guestName, guest_age_group: guestAgeGroup, guest_location: guestLocation,
        match_type: matchType, player1_name: p1Name, player2_name: p2Name,
        player1_partner_name: p1Partner, player2_partner_name: p2Partner,
        best_of: parseInt(bestOf), points_per_game: parseInt(pointsPerGame),
      });
      toast.success('Match created!');
      onCreated(res.data.match_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create match');
    } finally { setLoading(false); }
  };

  return (
    <Card className="max-w-lg mx-auto">
      <CardHeader>
        <CardTitle className="font-heading uppercase tracking-tight text-center">Quick Match</CardTitle>
        <p className="text-sm text-muted-foreground text-center">No sign-in needed. Just play!</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleCreate} className="space-y-5">
          {/* Your Info */}
          <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Your Info</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Your Name *</Label>
                <div className="relative"><User className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Name" value={guestName} onChange={e => setGuestName(e.target.value)} className="pl-8 h-9" required data-testid="guest-name-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Age Group</Label>
                <div className="relative"><UsersIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="e.g. U-15" value={guestAgeGroup} onChange={e => setGuestAgeGroup(e.target.value)} className="pl-8 h-9" data-testid="guest-age-group-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <div className="relative"><MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="City / Area" value={guestLocation} onChange={e => setGuestLocation(e.target.value)} className="pl-8 h-9" data-testid="guest-location-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Match Settings */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Match Type</Label>
              <Select value={matchType} onValueChange={setMatchType}>
                <SelectTrigger className="h-9" data-testid="guest-match-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="singles">Singles</SelectItem>
                  <SelectItem value="doubles">Doubles</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Points/Game</Label>
              <Select value={pointsPerGame} onValueChange={setPointsPerGame}>
                <SelectTrigger className="h-9" data-testid="guest-points-per-game"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="11">11 Points</SelectItem>
                  <SelectItem value="15">15 Points</SelectItem>
                  <SelectItem value="21">21 Points</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Best Of</Label>
              <Select value={bestOf} onValueChange={setBestOf}>
                <SelectTrigger className="h-9" data-testid="guest-best-of"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Game</SelectItem>
                  <SelectItem value="3">Best of 3</SelectItem>
                  <SelectItem value="5">Best of 5</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Players */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 p-3 rounded-lg border-2 border-primary/30 bg-primary/5">
              <p className="text-xs font-bold text-primary uppercase">Side A</p>
              <Input placeholder="Player 1 name *" value={p1Name} onChange={e => setP1Name(e.target.value)} className="h-9" required data-testid="guest-p1-name" />
              {matchType === 'doubles' && <Input placeholder="Partner name *" value={p1Partner} onChange={e => setP1Partner(e.target.value)} className="h-9" data-testid="guest-p1-partner" />}
            </div>
            <div className="space-y-2 p-3 rounded-lg border-2 border-secondary/30 bg-secondary/5">
              <p className="text-xs font-bold text-secondary uppercase">Side B</p>
              <Input placeholder="Player 2 name *" value={p2Name} onChange={e => setP2Name(e.target.value)} className="h-9" required data-testid="guest-p2-name" />
              {matchType === 'doubles' && <Input placeholder="Partner name *" value={p2Partner} onChange={e => setP2Partner(e.target.value)} className="h-9" data-testid="guest-p2-partner" />}
            </div>
          </div>

          <Button type="submit" className="w-full h-12 text-base font-bold" disabled={loading} data-testid="guest-create-match-btn">
            {loading ? 'Creating...' : 'Start Match'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function GuestMatchScoring({ matchId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [swapped, setSwapped] = useState(false);

  const fetchMatch = useCallback(async () => {
    try {
      const res = await guestAPI.getMatch(matchId);
      setMatch(res.data);
      if (res.data.started_at) {
        const started = new Date(res.data.started_at);
        setElapsedTime(Math.floor((Date.now() - started.getTime()) / 1000));
      }
    } catch { toast.error('Failed to load match'); }
    finally { setLoading(false); }
  }, [matchId]);

  useEffect(() => { fetchMatch(); }, [fetchMatch]);

  useEffect(() => {
    if (match?.status === 'live') {
      const t = setInterval(() => setElapsedTime(p => p + 1), 1000);
      return () => clearInterval(t);
    }
  }, [match?.status]);

  const handleStart = async () => {
    setUpdating(true);
    try { await guestAPI.startMatch(matchId); await fetchMatch(); toast.success('Match started!'); }
    catch { toast.error('Failed to start'); }
    finally { setUpdating(false); }
  };

  const handleScore = async (scoringPlayer) => {
    if (match?.status !== 'live') return;
    setUpdating(true);
    try {
      const res = await guestAPI.scoreMatch(matchId, { scoring_player: scoringPlayer });
      await fetchMatch();
      if (res.data.message?.includes('completed')) {
        const winnerName = res.data.winner === 'guest_p1' ? match.player1_name : match.player2_name;
        toast.success(`Match completed! Winner: ${winnerName}`);
      } else if (res.data.game_winner) {
        toast.info('Game won! Next game starting.');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to score'); }
    finally { setUpdating(false); }
  };

  const handleLet = async () => {
    if (match?.status !== 'live') return;
    setUpdating(true);
    try { await guestAPI.scoreMatch(matchId, { scoring_player: 'player1', is_let: true }); await fetchMatch(); }
    catch { toast.error('Failed'); }
    finally { setUpdating(false); }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/guest/${matchId}`;
    const p1 = match.player1_name;
    const p2 = match.player2_name;
    const currentGame = match.games?.[match.current_game - 1] || {};
    const scoreText = match.status === 'completed'
      ? `Final: ${p1} vs ${p2}`
      : `Live: ${p1} ${currentGame.player1_score || 0} - ${currentGame.player2_score || 0} ${p2}`;
    const text = `${scoreText} on LoveAll`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'LoveAll Match', text, url }); }
      catch (err) {
        // User cancelled share — silent debug only.
        if (process.env.NODE_ENV !== 'production') console.debug('guest share cancelled:', err?.message);
      }
    } else {
      window.open(whatsappUrl, '_blank');
    }
  };

  const getP1Name = () => swapped ? match.player2_name : match.player1_name;
  const getP2Name = () => swapped ? match.player1_name : match.player2_name;
  const getCurrentGame = () => {
    if (!match?.games?.length) return { player1_score: 0, player2_score: 0 };
    return match.games[match.current_game - 1] || { player1_score: 0, player2_score: 0 };
  };
  const getGamesWon = (player) => match?.games?.filter(g => g.winner === player).length || 0;

  if (loading) return <div className="space-y-4"><div className="h-12 bg-muted animate-pulse rounded" /><div className="h-64 bg-muted animate-pulse rounded" /></div>;
  if (!match) return null;

  const currentGame = getCurrentGame();
  const isCompleted = match.status === 'completed';
  const isLive = match.status === 'live';
  const isScheduled = match.status === 'scheduled';

  return (
    <div className="space-y-6" data-testid="guest-scoring-page">
      {/* Guest Info Banner */}
      {match.guest_info && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground bg-muted/30 rounded-lg px-4 py-2">
          <User className="h-3.5 w-3.5" />
          <span>{match.guest_info.name}</span>
          {match.guest_info.age_group && <><span className="text-border">|</span><span>{match.guest_info.age_group}</span></>}
          {match.guest_info.location && <><span className="text-border">|</span><span>{match.guest_info.location}</span></>}
        </div>
      )}

      {/* Match Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Badge className={`${getStatusColor(match.status)} text-sm px-4 py-2`}>
            {isLive && <span className="h-2 w-2 rounded-full bg-white mr-2 live-indicator" />}
            {match.status.toUpperCase()}
          </Badge>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span className="font-mono">{formatDuration(isLive ? elapsedTime : match.duration_seconds)}</span>
          </div>
          {match.match_type === 'doubles' && <Badge variant="outline" className="text-[10px]">Doubles</Badge>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setSwapped(!swapped)} data-testid="guest-swap-btn">
            <ArrowLeftRight className="mr-1 h-4 w-4" />Swap
          </Button>
          <Button variant="outline" size="sm" onClick={handleShare} data-testid="guest-share-btn">
            <Share2 className="mr-1 h-4 w-4" />Share
          </Button>
          {isLive && (
            <Button variant="outline" size="sm" onClick={handleLet} disabled={updating} data-testid="guest-let-btn">
              <RotateCcw className="mr-1 h-4 w-4" />Let
            </Button>
          )}
        </div>
      </div>

      {/* Score Display */}
      <Card className="overflow-hidden">
        <div className="bg-slate-900 p-6 sm:p-8">
          {/* Games Won */}
          {match.best_of > 1 && (
            <div className="flex justify-center gap-8 mb-4">
              <div className="text-center">
                <p className="text-slate-400 text-xs mb-1">Games</p>
                <div className="flex gap-1.5">{[...Array(Math.ceil(match.best_of / 2) + 1)].map((_, i) => (
                  <div key={`a-${i}`} className={`w-3 h-3 rounded-full ${i < getGamesWon('player1') ? 'bg-primary' : 'bg-slate-700'}`} />
                ))}</div>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-xs mb-1">Games</p>
                <div className="flex gap-1.5">{[...Array(Math.ceil(match.best_of / 2) + 1)].map((_, i) => (
                  <div key={`b-${i}`} className={`w-3 h-3 rounded-full ${i < getGamesWon('player2') ? 'bg-secondary' : 'bg-slate-700'}`} />
                ))}</div>
              </div>
            </div>
          )}

          {/* Main Score */}
          <div className="grid grid-cols-3 items-center gap-4">
            <div className="text-center">
              <p className="text-white font-heading text-lg sm:text-xl font-bold">{getP1Name()}</p>
              {match.match_type === 'doubles' && (
                <p className="text-white font-heading text-lg sm:text-xl font-bold">& {swapped ? match.player2_partner_name : match.player1_partner_name}</p>
              )}
              {((swapped ? 'guest_p2' : 'guest_p1') === match.current_server) && isLive && (
                <div className="flex justify-center mt-1"><div className="service-indicator" title="Serving" /></div>
              )}
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-3">
                <span className="font-heading text-6xl sm:text-7xl font-black text-primary tabular-nums">
                  {swapped ? currentGame.player2_score : currentGame.player1_score}
                </span>
                <span className="text-slate-500 text-3xl">-</span>
                <span className="font-heading text-6xl sm:text-7xl font-black text-secondary tabular-nums">
                  {swapped ? currentGame.player1_score : currentGame.player2_score}
                </span>
              </div>
              {isLive && <p className="text-slate-400 mt-2 text-sm">Game {match.current_game} &middot; {match.service_side === 'right' ? 'Right' : 'Left'}</p>}
            </div>
            <div className="text-center">
              <p className="text-white font-heading text-lg sm:text-xl font-bold">{getP2Name()}</p>
              {match.match_type === 'doubles' && (
                <p className="text-white font-heading text-lg sm:text-xl font-bold">& {swapped ? match.player1_partner_name : match.player2_partner_name}</p>
              )}
              {((swapped ? 'guest_p1' : 'guest_p2') === match.current_server) && isLive && (
                <div className="flex justify-center mt-1"><div className="service-indicator" title="Serving" /></div>
              )}
            </div>
          </div>

          {/* Game History */}
          {match.games.length > 1 && (
            <div className="mt-4 flex justify-center gap-3">
              {match.games.map((g, i) => (
                <div key={`gm-game-${i}-${g.player1_score}-${g.player2_score}`} className={`px-3 py-1.5 rounded ${i === match.current_game - 1 ? 'bg-slate-700' : 'bg-slate-800'}`}>
                  <p className="text-[10px] text-slate-400">Game {i + 1}</p>
                  <p className="font-mono text-white text-sm">{g.player1_score}-{g.player2_score}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Controls */}
      {!isCompleted && (
        <div className="space-y-4">
          {isScheduled && (
            <Button className="w-full h-14 text-xl font-bold bg-secondary hover:bg-secondary/90" onClick={handleStart} disabled={updating} data-testid="guest-start-btn">
              <Play className="mr-2 h-6 w-6" />Start Match
            </Button>
          )}
          {isLive && (
            <div className="grid grid-cols-2 gap-4">
              <Button className="h-28 sm:h-32 text-xl sm:text-2xl font-heading font-bold bg-primary hover:bg-primary/90 score-btn"
                onClick={() => handleScore(swapped ? 'player2' : 'player1')} disabled={updating} data-testid="guest-score-p1">
                +1 {getP1Name().split(' ')[0]}
              </Button>
              <Button className="h-28 sm:h-32 text-xl sm:text-2xl font-heading font-bold bg-secondary hover:bg-secondary/90 score-btn"
                onClick={() => handleScore(swapped ? 'player1' : 'player2')} disabled={updating} data-testid="guest-score-p2">
                +1 {getP2Name().split(' ')[0]}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Match Completed */}
      {isCompleted && (
        <Card className="border-green-500/50 bg-green-500/5">
          <CardContent className="py-8 text-center">
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
            <h2 className="font-heading text-2xl font-bold mb-2">Match Completed!</h2>
            <p className="text-lg text-muted-foreground">
              Winner: <span className="text-green-500 font-bold">{match.winner_id === 'guest_p1' ? match.player1_name : match.player2_name}</span>
            </p>
            <p className="text-muted-foreground mt-1">Duration: {formatDuration(match.duration_seconds)}</p>
            <div className="flex gap-3 justify-center mt-6">
              <Button onClick={handleShare} data-testid="guest-share-result-btn"><Share2 className="mr-2 h-4 w-4" />Share Result</Button>
              <Button variant="outline" onClick={() => navigate('/guest')} data-testid="guest-new-match-btn">New Match</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Game-by-Game History */}
      {match.games?.length > 1 && (
        <Card>
          <CardHeader><CardTitle className="font-heading uppercase tracking-tight text-base">Game History</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {match.games.map((g, i) => {
                const p1S = g.player1_score || 0, p2S = g.player2_score || 0;
                const p1Won = g.winner === 'player1', p2Won = g.winner === 'player2';
                const w = Math.max(5, Math.round((p1S / Math.max(p1S + p2S, 1)) * 100));
                return (
                  <div key={`gm-hist-${i}-${p1S}-${p2S}`} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-14 shrink-0">Game {i + 1}</span>
                    <div className="flex-1 h-7 rounded-full overflow-hidden flex bg-muted">
                      <div className={`h-full flex items-center justify-end pr-2 text-xs font-mono font-bold ${p1Won ? 'bg-primary text-primary-foreground' : 'bg-primary/30'}`} style={{ width: `${w}%` }}>{p1S}</div>
                      <div className={`h-full flex items-center justify-start pl-2 text-xs font-mono font-bold ${p2Won ? 'bg-secondary text-secondary-foreground' : 'bg-secondary/30'}`} style={{ width: `${100 - w}%` }}>{p2S}</div>
                    </div>
                    {(p1Won || p2Won) && <Badge variant="outline" className="text-[10px] shrink-0">{p1Won ? match.player1_name.split(' ')[0] : match.player2_name.split(' ')[0]}</Badge>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function GuestMatch() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [activeMatchId, setActiveMatchId] = useState(matchId || null);

  const handleCreated = (id) => {
    setActiveMatchId(id);
    navigate(`/guest/${id}`, { replace: true });
  };

  return (
    <Layout>
      <div data-testid="guest-match-page">
        {activeMatchId ? (
          <GuestMatchScoring matchId={activeMatchId} />
        ) : (
          <GuestMatchCreate onCreated={handleCreated} />
        )}
      </div>
    </Layout>
  );
}
